
const frontMatter = require("front-matter");
const md = require("markdown-it")({
    html: true,
    linkify: true,
    typographer: true,
});

const images = require.context("@/content/blog", true, /\.(png|jpe?g|svg)$/);

/**
 * loads all markdown files in the content/blog directory
 * @returns {Array} List of blog posts sorted by date
 */
export function getBlogPosts(lang = "en") {
    const context = require.context("@/content/blog", true, /\.md$/);
    const posts = [];

    context.keys().forEach((key) => {
        // key is like ./slug/index.en.md
        if (!key.endsWith(`.${lang}.md`)) return;

        const content = context(key).default || context(key);
        const attributes = frontMatter(content).attributes;

        // Extract slug from path: ./hello-world/index.en.md -> hello-world
        const slug = key.split("/")[1];

        // Try to resolve thumbnail
        // Try to resolve thumbnail
        let thumbnail = null;
        if (attributes.thumbnail) {
            try {
                // If it is a relative path (starts with ./), resolve it using the images context
                if (attributes.thumbnail.startsWith("./")) {
                    const imageName = attributes.thumbnail.replace(/^\.\//, "");
                    thumbnail = images(`./${slug}/${imageName}`);
                } else {
                    // Use as is (absolute path or external URL)
                    thumbnail = attributes.thumbnail;
                }
            } catch (e) {
                console.warn(`Failed to process thumbnail for ${slug}:`, e);
            }
        }

        // Fallback to default thumbnail.png if not specified
        if (!thumbnail) {
            try {
                thumbnail = images(`./${slug}/thumbnail.png`);
            } catch (e) {
                // No default thumbnail found
            }
        }

        posts.push({
            slug,
            ...attributes,
            thumbnail,
        });
    });

    return posts.sort((a, b) => new Date(b.date) - new Date(a.date));
}

/**
 * Loads a specific blog post
 * @param {string} slug 
 * @param {string} lang 
 */
export function getBlogPost(slug, lang = "en") {
    try {
        // Note: dynamic import with template strings works in Webpack if the pattern is clear
        // But since we are using require.context separately or raw-loader, we can try robust approach.
        // However, require(`@/content/blog/${slug}/index.${lang}.md`) is the standard way.

        const content = require(`@/content/blog/${slug}/index.${lang}.md`).default;
        const { attributes, body } = frontMatter(content);

        // Render markdown
        let html = md.render(body);

        // FIX: Image paths in markdown (e.g. ./thumbnail.png) won't work out of the box because 
        // raw-loader just gives string. We need to manually replace them or use a different loader strategy.
        // BUT, for simplicity in this "migration-ready" setup, we can recommend placing images in public/ 
        // OR we can do a quick regex replacement if we really want co-located images to work.
        // 
        // For now, to make "./thumbnail.png" work, we might need to assume images are handled.
        // A common trick is to use require inside the component or process the HTML.
        // 
        // Update: Simplest way for Vue + Webpack to handle markdown images is unclear without 'vue-markdown-loader'.
        // Given the constraints, I will add a helper to resolve image paths if possible, 
        // OR standard advice: put images in static folder OR use absolute paths.
        // 
        // Let's implement basic rendering first.

        return {
            slug,
            ...attributes,
            html,
        };
    } catch (e) {
        console.error("Failed to load blog post", e);
        return null;
    }
}

export function getBlogImage(slug, filename) {
    try {
        // filename might be "./thumbnail.png" -> need "./slug/thumbnail.png"
        // or "thumbnail.png"
        const name = filename.replace(/^\.\//, "");
        const key = `./${slug}/${name}`;
        return images(key);
    } catch (e) {
        return null;
    }
}
